/**
 * Word Text Edit Operations
 *
 * Handles text-level EDIT operations (replace, before, after) for auto-redline.
 * Extracted from wordBatchEditOperations.ts to follow Single Responsibility Principle.
 */

/* global Word */

import { EditOperation, EditApplicationResult } from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import {
  searchDocument,
  searchWithinParagraph,
  selectOccurrence,
  searchWithinRange,
  SearchResult,
} from "./wordDocumentSearch";
import { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";

const log = createLogger("WordTextEditOperations");

/**
 * Apply a text EDIT operation using document-wide or paragraph-scoped search
 *
 * Searches for searchPhrase in the document (or specific paragraph if paragraphId provided),
 * selects the correct occurrence, and applies the edit based on position (replace/before/after).
 *
 * When paragraphId is provided:
 * - Uses indexTracker to calculate adjusted paragraph index (accounts for INSERT/DELETE)
 * - Searches ONLY within that paragraph
 * - searchPhraseOccurrence counts within that paragraph only
 *
 * When paragraphId is NOT provided (backward compatibility):
 * - Searches entire document
 * - searchPhraseOccurrence counts document-wide
 *
 * NOTE: EDIT operations do NOT record to tracker - they don't shift indices
 *
 * @param edit - The edit operation to apply
 * @param context - Word request context
 * @param startTime - Operation start time for duration tracking
 * @param paragraphId - Optional paragraph index from AutoRedlineOutput (0-based)
 * @param indexTracker - Optional tracker for paragraphId index adjustment
 * @returns Promise of operation result
 */
export async function applyTextEditOperation(
  edit: EditOperation,
  context: Word.RequestContext,
  startTime: number,
  paragraphId?: number,
  indexTracker?: UnifiedIndexTracker
): Promise<EditApplicationResult> {
  let searchResult: SearchResult;

  // Check if this EDIT operation uses paragraphId for scoped search
  if (paragraphId !== undefined) {
    // Calculate adjusted index accounting for previous INSERT/DELETE operations
    const adjustedIndex = indexTracker
      ? indexTracker.calculateAdjustedIndexForEdit(paragraphId)
      : paragraphId;

    // CS-259: Log paragraph adjustment for EDIT operations
    if (indexTracker && adjustedIndex !== paragraphId) {
      log.info(`CS-259: EDIT paragraph index adjusted`, {
        editId: edit.editId,
        originalParagraphId: paragraphId,
        adjustedParagraphId: adjustedIndex,
        adjustment: adjustedIndex - paragraphId,
        trackedOperations: indexTracker.getOperationCount(),
      });
    }

    // Empty searchPhrase: directly insert text into the paragraph (no search needed)
    // This handles filling EXISTING empty paragraphs without creating new ones
    if (edit.searchPhrase === "") {
      log.debug(`Empty searchPhrase for ${edit.editId}, using direct paragraph insertion`, {
        paragraphId,
        adjustedIndex,
        position: edit.position,
      });
      return applyDirectParagraphEdit(edit, context, adjustedIndex, startTime);
    }

    log.debug(`Paragraph-scoped search for ${edit.editId}`, {
      originalParagraphId: paragraphId,
      adjustedIndex,
      searchPhrase:
        edit.searchPhrase.substring(0, 50) + (edit.searchPhrase.length > 50 ? "..." : ""),
      hasTracker: !!indexTracker,
    });

    try {
      searchResult = await searchWithinParagraph(context, adjustedIndex, edit.searchPhrase);
    } catch (error) {
      // Handle paragraph bounds errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error(`Edit failed: ${edit.editId}`, {
        error: errorMessage,
        paragraphId,
        adjustedIndex,
      });
      return {
        success: false,
        editId: edit.editId,
        error: errorMessage,
        duration: Date.now() - startTime,
      };
    }
  } else {
    // Document-wide search for searchPhrase using 2-tier fallback (backward compatible)
    searchResult = await searchDocument(context, edit.searchPhrase);
  }

  log.debug(`Search completed for ${edit.editId}`, {
    occurrencesFound: searchResult.totalOccurrences,
    usedFallback: searchResult.usedFallback,
    searchType: searchResult.usedFallback ? "STRICT" : "LENIENT",
    searchScope: paragraphId !== undefined ? `paragraph ${paragraphId}` : "document-wide",
  });

  // Validate that search found something
  if (searchResult.totalOccurrences === 0) {
    const searchScope = paragraphId !== undefined ? `paragraph ${paragraphId}` : "document";
    const error = `Search phrase not found in ${searchScope}: "${edit.searchPhrase}"`;
    log.error(`Edit failed: ${edit.editId}`, {
      error,
      searchPhrase: edit.searchPhrase,
      searchPhraseLength: edit.searchPhrase.length,
      searchScope,
    });

    return {
      success: false,
      editId: edit.editId,
      error,
      duration: Date.now() - startTime,
    };
  }

  // Select the specific occurrence using searchPhraseOccurrence
  const selectedRange = selectOccurrence(
    searchResult.results,
    edit.searchPhraseOccurrence,
    edit.searchPhrase,
    edit.editId
  );

  log.debug(`Selected occurrence ${edit.searchPhraseOccurrence} for ${edit.editId}`, {
    totalFound: searchResult.totalOccurrences,
    usedFallback: searchResult.usedFallback,
  });

  // Apply the edit based on position
  const editResult = await applyEditByPosition(edit, selectedRange, context);
  if (!editResult.success) {
    return {
      success: false,
      editId: edit.editId,
      error: editResult.error,
      duration: Date.now() - startTime,
    };
  }

  // Apply the edit with sync
  await context.sync();

  log.info(`Edit operation completed for ${edit.editId}`, {
    position: edit.position,
    duration: Date.now() - startTime,
  });

  return {
    success: true,
    editId: edit.editId,
    duration: Date.now() - startTime,
  };
}

/** Result from position-based edit application */
interface PositionEditResult {
  success: boolean;
  error?: string;
}

/**
 * Apply edit based on position type (replace/before/after)
 */
async function applyEditByPosition(
  edit: EditOperation,
  selectedRange: Word.Range,
  context: Word.RequestContext
): Promise<PositionEditResult> {
  switch (edit.position) {
    case "replace":
      return applyReplaceEdit(edit, selectedRange, context);

    case "before":
      selectedRange.insertText(edit.replacementText, Word.InsertLocation.start);
      log.debug(`Inserted text before searchPhrase for ${edit.editId}`);
      return { success: true };

    case "after":
      selectedRange.insertText(edit.replacementText, Word.InsertLocation.end);
      log.debug(`Inserted text after searchPhrase for ${edit.editId}`);
      return { success: true };

    default:
      const error = `Unknown position: ${edit.position}`;
      log.error(`Edit failed: ${edit.editId}`, { error });
      return { success: false, error };
  }
}

/**
 * Apply a replace edit operation
 *
 * If targetText is specified, replaces only that text within the searchPhrase.
 * Otherwise, replaces the entire searchPhrase.
 *
 * Uses 2-step approach for consistent behavior across Desktop and Online:
 * 1. Insert replacement text after the target
 * 2. Delete the original target text
 */
async function applyReplaceEdit(
  edit: EditOperation,
  selectedRange: Word.Range,
  context: Word.RequestContext
): Promise<PositionEditResult> {
  if (edit.targetText) {
    // Search for targetText within selected searchPhrase occurrence
    const targetSearch = await searchWithinRange(
      selectedRange,
      edit.targetText,
      edit.matchWholeWord ?? false,
      context
    );

    log.debug(`Target search for ${edit.editId}`, {
      targetText: edit.targetText,
      occurrencesFound: targetSearch.items.length,
    });

    // Use targetOccurrence to select which occurrence of targetText
    const targetOccurrence = edit.targetOccurrence ?? 1;
    const targetIndex = targetOccurrence - 1;

    if (targetSearch.items.length === 0) {
      const error = `Target text "${edit.targetText}" not found within selected searchPhrase occurrence`;
      log.error(`Edit failed: ${edit.editId}`, { error });
      return { success: false, error };
    }

    if (targetIndex >= targetSearch.items.length) {
      const error = `Target text "${edit.targetText}" occurrence ${targetOccurrence} not found (only ${targetSearch.items.length} found)`;
      log.error(`Edit failed: ${edit.editId}`, { error });
      return { success: false, error };
    }

    // Replace the specified occurrence of targetText using 2-step approach
    const targetRange = targetSearch.items[targetIndex];
    targetRange.insertText(edit.replacementText, Word.InsertLocation.after);
    targetRange.insertText("", Word.InsertLocation.replace);
    log.debug(`Replaced targetText occurrence ${targetOccurrence} for ${edit.editId}`);
  } else {
    // Replace entire searchPhrase using 2-step approach
    selectedRange.insertText(edit.replacementText, Word.InsertLocation.after);
    selectedRange.insertText("", Word.InsertLocation.replace);
    log.debug(`Replaced entire searchPhrase for ${edit.editId}`);
  }

  return { success: true };
}

/**
 * Apply text directly to a paragraph without search (for empty paragraphs)
 *
 * Used when searchPhrase is empty and paragraphId is provided. Bypasses search
 * and inserts text directly into the target paragraph. This handles filling
 * EXISTING empty paragraphs without creating new ones (which INSERT would do).
 */
async function applyDirectParagraphEdit(
  edit: EditOperation,
  context: Word.RequestContext,
  adjustedIndex: number,
  startTime: number
): Promise<EditApplicationResult> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
    return {
      success: false,
      editId: edit.editId,
      error: `Paragraph index ${adjustedIndex} out of bounds (document has ${paragraphs.items.length} paragraphs)`,
      duration: Date.now() - startTime,
    };
  }

  const targetParagraph = paragraphs.items[adjustedIndex];

  switch (edit.position) {
    case "before":
      targetParagraph.insertText(edit.replacementText, Word.InsertLocation.start);
      break;
    case "after":
      targetParagraph.insertText(edit.replacementText, Word.InsertLocation.end);
      break;
    case "replace":
      targetParagraph.insertText(edit.replacementText, Word.InsertLocation.replace);
      break;
    default:
      return {
        success: false,
        editId: edit.editId,
        error: `Unknown position: ${edit.position}`,
        duration: Date.now() - startTime,
      };
  }
  await context.sync();

  log.info(`Direct paragraph edit completed for ${edit.editId}`, {
    adjustedIndex,
    position: edit.position,
    textLength: edit.replacementText.length,
    duration: Date.now() - startTime,
  });

  return {
    success: true,
    editId: edit.editId,
    duration: Date.now() - startTime,
  };
}

/**
 * Apply text edit within a batch operation (no sync)
 *
 * Used by batch operations where sync is deferred until all edits are queued.
 * Does not call context.sync() - caller is responsible for syncing.
 *
 * @param edit - The edit operation to apply
 * @param searchPhraseRange - Pre-selected range from batch search phase
 * @param targetSearch - Pre-executed target search results (if applicable)
 * @returns Edit result (success/failure with optional error)
 */
export function applyTextEditInBatch(
  edit: EditOperation,
  searchPhraseRange: Word.Range,
  targetSearch?: Word.RangeCollection
): PositionEditResult {
  switch (edit.position) {
    case "replace":
      return applyReplaceEditSync(edit, searchPhraseRange, targetSearch);

    case "before":
      searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.start);
      log.debug(`Inserted text before searchPhrase for ${edit.editId}`);
      return { success: true };

    case "after":
      searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.end);
      log.debug(`Inserted text after searchPhrase for ${edit.editId}`);
      return { success: true };

    default:
      const error = `Unknown position: ${edit.position}`;
      log.warn(`Edit ${edit.editId} failed: ${error}`);
      return { success: false, error };
  }
}

/**
 * Apply replace edit synchronously (no async operations)
 *
 * Used within batch operations where search has already been performed.
 */
function applyReplaceEditSync(
  edit: EditOperation,
  searchPhraseRange: Word.Range,
  targetSearch?: Word.RangeCollection
): PositionEditResult {
  if (edit.targetText && targetSearch) {
    const targetOccurrence = edit.targetOccurrence ?? 1;
    const targetIndex = targetOccurrence - 1;

    if (targetSearch.items.length === 0) {
      const error = `Target text "${edit.targetText}" not found within selected searchPhrase occurrence`;
      log.warn(`Edit ${edit.editId} failed: ${error}`);
      return { success: false, error };
    }

    if (targetIndex >= targetSearch.items.length) {
      const error = `Target text "${edit.targetText}" occurrence ${targetOccurrence} not found (only ${targetSearch.items.length} found)`;
      log.warn(`Edit ${edit.editId} failed: ${error}`);
      return { success: false, error };
    }

    // Replace the specified occurrence using 2-step approach
    const targetRange = targetSearch.items[targetIndex];
    targetRange.insertText(edit.replacementText, Word.InsertLocation.after);
    targetRange.insertText("", Word.InsertLocation.replace);
    log.debug(`Replaced targetText occurrence ${targetOccurrence} for ${edit.editId}`);
  } else {
    // Replace entire searchPhrase using 2-step approach
    searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.after);
    searchPhraseRange.insertText("", Word.InsertLocation.replace);
    log.debug(`Replaced entire searchPhrase for ${edit.editId}`);
  }

  return { success: true };
}
